import React from "react";

import { useIntl } from "react-intl";

import * as Table from "ds/Table";
import Tag from "ds/Tag";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { integrationsSelector } from "Reducers/integration";
import { SortingValues } from "Reducers/integration/types";
import { useObservabilitySuiteIntegrationList } from "src/organization/pages/project/settings/integrations/components/IntegrationForm/config";
import IntegrationIcon from "src/organization/pages/project/settings/integrations/components/IntegrationIcon";

import {
  Line,
  List,
  Flex,
  Type,
  TableThead,
  TableTh
} from "./IntegrationList.styles";

import type { Integration } from "@packages/client";
import type { SortDirections } from "@packages/ds";

export interface ComponentState {
  category: SortDirections;
  id: SortDirections;
  type: SortDirections;
  sortBy: SortingValues;
}

export interface Action {
  type: SortingValues;
}

const IntegrationList = () => {
  const intl = useIntl();

  const [sort, setSort] = Table.useSort<{
    type: string;
    category: string;
    id: string;
  }>({
    key: "type",
    direction: "asc"
  });

  const integrations: ReturnType<typeof integrationsSelector> =
    useSelectorWithUrlParams(integrationsSelector, {
      sortBy: sort.key,
      sortOrder: sort.direction
    });

  return (
    <div>
      <Table.Table>
        <TableThead>
          <TableTh
            mediaWidths={["40%", "50%"]}
            onSortChanged={() => {
              setSort("type");
            }}
            isSortActive={sort.key === "type"}
            sortDirection={sort.direction}
          >
            {intl.formatMessage({
              id: "integration.table.headers.integration"
            })}
          </TableTh>
          <TableTh
            mediaWidths={["30%", "25%"]}
            onSortChanged={() => {
              setSort("category");
            }}
            isSortActive={sort.key === "category"}
            sortDirection={sort.direction}
          >
            {intl.formatMessage({ id: "integration.table.headers.category" })}
          </TableTh>
          <TableTh
            mediaWidths={["30%", "25%"]}
            onSortChanged={() => {
              setSort("id");
            }}
            isSortActive={sort.key === "id"}
            sortDirection={sort.direction}
          >
            {intl.formatMessage({ id: "integration.table.headers.id" })}
          </TableTh>
        </TableThead>
      </Table.Table>
      <List>
        {integrations.map(i => (
          <li key={`node-${i.id}`}>
            <IntegrationElement key={i.id} integration={i} />
          </li>
        ))}
      </List>
    </div>
  );
};

type IntegrationElementProps = {
  integration: Integration;
};

const IntegrationElement = ({ integration }: IntegrationElementProps) => {
  const intl = useIntl();
  const integrationList = useObservabilitySuiteIntegrationList();
  const obsConfig = integrationList.integrationConfig.find(
    cfg => cfg.type === integration.type
  );

  return (
    <Line to={`${integration.id}`}>
      <Type width={["40%", "50%"]}>
        <IntegrationIcon type={integration.type} />
        <span>
          {intl.formatMessage({
            id: `integration.type.${integration.type}`,
            defaultMessage: integration.type
          })}
        </span>
      </Type>
      <Flex width={["30%", "25%"]}>
        <Tag variant="gray">
          {obsConfig
            ? capitalize(
                intl.formatMessage({
                  id: `integrations.category.${obsConfig.category}`
                })
              )
            : capitalize(integration.category)}
        </Tag>
      </Flex>
      <Flex width={["30%", "25%"]}>{integration.id}</Flex>
    </Line>
  );
};

export default IntegrationList;
